import React, { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { usePlanFeatures } from '../../contexts/PlanFeaturesContext';
import ExternalLinkModal from '../externalLinkModal/externalLinkModal';
import './styles.css';

const Footer = () => {
    const { getFooterLinks, getStateName, planFeatures, loading, error } = usePlanFeatures();

    const footerLinks = getFooterLinks();
    const stateName = getStateName();
    // Mercy Care (AZ) plans get a different 3-link footer set than every other state:
    // "Privacy and legal" (combined privacy+legal link), plus separate nondiscrimination
    // notices for the base Mercy Care plan and for Mercy Care Advantage — replacing the
    // standard Privacy policy / Legal statement / Nondiscrimination notice trio below.
    const isMercyCare = stateName === 'Mercy Care';

    // "You are now leaving..." confirmation modal for external links embedded in
    // planFeatures.footerText's raw HTML (dangerouslySetInnerHTML below, same pattern
    // as infoTooltip.jsx's tooltip body). Those are plain DOM anchors with no React
    // click handler of their own, so clicks are intercepted via event delegation
    // (handleFooterTextClick) rather than per-link React props.
    const [externalLinkModalOpen, setExternalLinkModalOpen] = useState(false);
    const [pendingExternalUrl, setPendingExternalUrl] = useState(null);

    const handleFooterTextClick = useCallback((e) => {
        const anchor = e.target.closest('a[href]');
        if (anchor) {
            e.preventDefault();
            setPendingExternalUrl(anchor.href);
            setExternalLinkModalOpen(true);
        }
    }, []);

    const handleExternalLinkConfirm = useCallback(() => {
        if (pendingExternalUrl) {
            window.open(pendingExternalUrl, '_blank', 'noopener,noreferrer');
        }
        setExternalLinkModalOpen(false);
        setPendingExternalUrl(null);
    }, [pendingExternalUrl]);

    const handleExternalLinkCancel = useCallback(() => {
        setExternalLinkModalOpen(false);
        setPendingExternalUrl(null);
    }, []);

    return (
        <footer className="container-fluid">
           
            {/* Footer legal links using API URLs.
                A11y fix: this row of links visually reads as a list but was previously
                just bare sibling <a> tags in a flex <div> — no semantic list structure
                (so AT never announced "list of 3 items"/allowed list navigation), and no
                landmark identifying it as footer/site navigation. Wrapped in <nav
                aria-label="Footer"> (a landmark AT users can jump straight to) containing
                a real <ul>/<li> list; .footer-inner's existing flex-row layout is now on
                the <ul> itself (list-style/margin/padding reset in styles.css) so the
                visual layout is unchanged. */}
            <nav aria-label="Footer" className="footer-legal-links">
                <ul className="footer-inner">
                  {isMercyCare ? (
                    <>
                      <li>
                          <a
                              href={footerLinks.mercyCarePrivacyAndLegal || '#'}
                              className="footer-legal-link footer-privacy-link"
                              target="_blank"
                              rel="noopener noreferrer"
                              title={footerLinks.mercyCarePrivacyAndLegal ? 'Privacy and legal' : 'Privacy and legal (URL not available)'}
                          >
                              Privacy and legal
                          </a>
                      </li>
                      <li>
                          <a
                              href={footerLinks.mercyCareNonDiscrimination || '#'}
                              className="footer-legal-link footer-nondiscrimination-link"
                              target="_blank"
                              rel="noopener noreferrer"
                              title={footerLinks.mercyCareNonDiscrimination ? 'Mercy Care nondiscrimination notice' : 'Mercy Care nondiscrimination notice (URL not available)'}
                          >
                              Mercy Care nondiscrimination notice
                          </a>
                      </li>
                      <li>
                          <a
                              href={footerLinks.mercyCareAdvantageNonDiscrimination || '#'}
                              className="footer-legal-link footer-nondiscrimination-link"
                              target="_blank"
                              rel="noopener noreferrer"
                              title={footerLinks.mercyCareAdvantageNonDiscrimination ? 'Mercy Care Advantage nondiscrimination notice' : 'Mercy Care Advantage nondiscrimination notice (URL not available)'}
                          >
                              Mercy Care Advantage nondiscrimination notice
                          </a>
                      </li>
                    </>
                  ) : (
                    <>
                      <li>
                          <a
                              href={footerLinks.privacyPolicy || '#'}
                              className="footer-legal-link footer-privacy-link"
                              target="_blank"
                              rel="noopener noreferrer"
                              title={footerLinks.privacyPolicy ? 'Privacy Policy' : 'Privacy Policy (URL not available)'}
                          >
                              Privacy policy
                          </a>
                      </li>
                      <li>
                          <a
                              href={footerLinks.legalStatement || '#'}
                              className="footer-legal-link footer-legal-statement-link"
                              target="_blank"
                              rel="noopener noreferrer"
                              title={footerLinks.legalStatement ? 'Legal Statement' : 'Legal Statement (URL not available)'}
                          >
                              Legal statement
                          </a>
                      </li>
                      <li>
                          <a
                              href={footerLinks.nonDiscrimination || '#'}
                              className="footer-legal-link footer-nondiscrimination-link"
                              target="_blank"
                              rel="noopener noreferrer"
                              title={footerLinks.nonDiscrimination ? 'Non-discrimination notice' : 'Non-discrimination notice (URL not available)'}
                          >
                              Nondiscrimination notice
                          </a>
                      </li>
                    </>
                  )}
                </ul>
            </nav>

            <div id="purpleBitWrapper">
                <div id="purpleBit">
                    {typeof planFeatures?.footerText === 'string' && /<p\b[^>]*>/i.test(planFeatures.footerText) ? (
                        <span
                            onClick={handleFooterTextClick}
                            dangerouslySetInnerHTML={{ __html: planFeatures.footerText }}
                        />
                    ) : (
                        planFeatures?.footerText || `Copyright © 2026 Aetna Better Health of ${stateName}, All Rights Reserved.`
                    )}
                </div>
            </div>

            {/* Rendered via portal to document.body — same rationale as
                infoTooltip.jsx's ExternalLinkModal portal: needs to escape any
                transformed ancestors (e.g. .results-page's transform: translateZ(0),
                see results/styles.css) to render as a proper centered fullscreen
                overlay instead of clipped/mispositioned. */}
            {externalLinkModalOpen && createPortal(
                <ExternalLinkModal
                    isOpen={externalLinkModalOpen}
                    onConfirm={handleExternalLinkConfirm}
                    onCancel={handleExternalLinkCancel}
                    url={pendingExternalUrl}
                />,
                document.body
            )}
        </footer>
    );
};

export default Footer;
